import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import { FileText, Pencil, Trash, Printer } from 'lucide-react';

export default function AbaAnamnese({ pacienteEditando }) {
  const [modelosAnamnese, setModelosAnamnese] = useState([]);
  const [modelosLoading, setModelosLoading] = useState(false);
  const [modelosErro, setModelosErro] = useState('');
  
  const [selectedModeloId, setSelectedModeloId] = useState(null);
  const [selectedModelo, setSelectedModelo] = useState(null);
  const [perguntasAnamnese, setPerguntasAnamnese] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [savingAnamnese, setSavingAnamnese] = useState(false);
  const [showSelectModelo, setShowSelectModelo] = useState(false);
  const [modeloEscolhidoId, setModeloEscolhidoId] = useState('');
  const [anamnesesPreenchidas, setAnamnesesPreenchidas] = useState([]);
  
  const [detalheAbertoId, setDetalheAbertoId] = useState(null);
  const [detalheCarregando, setDetalheCarregando] = useState(false);
  const [detalheDados, setDetalheDados] = useState(null);
  
  const [confirmRemoverId, setConfirmRemoverId] = useState(null);
  const [preenchimentoEditandoId, setPreenchimentoEditandoId] = useState(null);

  const cardColors = ['#5956D6', '#2B8FF2', '#ECAD21', '#03A400', '#DA5D5C', '#926AFF', '#568BEF', '#ECAD21', '#FF7FAF', '#926AFF', '#009AF3'];

  // Carrega modelos disponíveis
  useEffect(() => {
    if (!pacienteEditando) return;
    let alive = true;
    (async () => {
      try {
        setModelosErro('');
        setModelosLoading(true);
        const { data } = await axios.get('/api/anamnese/modelos');
        if (!alive) return;
        setModelosAnamnese(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setModelosErro('Não foi possível carregar os modelos de anamnese.');
      } finally {
        if (alive) setModelosLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [pacienteEditando?.id]);

  // Carrega preenchimentos anteriores do paciente
  useEffect(() => {
    if (!pacienteEditando?.id) return;
    let vivo = true;
    (async () => {
      try {
        const { data } = await axios.get(`/api/anamnese/preenchimentos/paciente/${pacienteEditando.id}`);
        if (!vivo) return;
        setAnamnesesPreenchidas(Array.isArray(data) ? data : []);
      } catch {
        if (!vivo) return;
        setAnamnesesPreenchidas([]);
      }
    })();
    return () => { vivo = false; };
  }, [pacienteEditando?.id]);

  function renderPergunta(p) {
    const key = String(p.id);
    const tipo = p.tipo || 'snn';
    
    if (tipo === 'texto') {
      return (
        <textarea 
          className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300" 
          value={respostas[key]?.texto || ''} 
          onChange={e => setRespostas(r => ({ ...r, [key]: { ...r[key], texto: e.target.value } }))} 
          placeholder="Resposta" 
        />
      );
    }
    
    if (tipo === 'snn' || tipo === 'snn_texto') {
      return (
        <div className="space-y-3 mt-2">
          <div className="flex gap-4 items-center">
            {['sim','nao','nao_sei'].map(opt => (
              <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name={`p-${key}`} 
                  checked={(respostas[key]?.opcao || '') === opt} 
                  onChange={() => setRespostas(r => ({ ...r, [key]: { ...r[key], opcao: opt } }))} 
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {opt === 'sim' ? 'Sim' : opt === 'nao' ? 'Não' : 'Não sei'}
                </span>
              </label>
            ))}
          </div>
          {tipo === 'snn_texto' && (
            <textarea 
              className="w-full border rounded px-3 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm" 
              value={respostas[key]?.texto || ''} 
              onChange={e => setRespostas(r => ({ ...r, [key]: { ...r[key], texto: e.target.value } }))} 
              placeholder="Descreva / Observações" 
            />
          )}
        </div>
      );
    }
    return null;
  }

  async function salvarAnamnesePaciente() {
    setSavingAnamnese(true);
    try {
      toast.dismiss && toast.dismiss();
      if (!selectedModeloId) {
        toast.error('Selecione um modelo.');
        return;
      }
      
      await axios.post('/api/anamnese/preenchimentos', {
        paciente_id: pacienteEditando.id,
        modelo_id: selectedModeloId,
        respostas
      });
      
      const { data } = await axios.get(`/api/anamnese/preenchimentos/paciente/${pacienteEditando.id}`);
      setAnamnesesPreenchidas(Array.isArray(data) ? data : []);
      toast.success('Anamnese salva com sucesso.');
      
      setShowSelectModelo(false);
      resetarSelecao();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar anamnese.');
    } finally {
      setSavingAnamnese(false);
    }
  }

  async function abrirDetalhePreenchimento(id) {
    setDetalheAbertoId(id);
    setDetalheCarregando(true);
    setDetalheDados(null);
    try {
      const { data } = await axios.get(`/api/anamnese/preenchimentos/${id}`);
      setDetalheDados(data || null);
    } catch {
      toast.error('Erro ao carregar os detalhes.');
    } finally {
      setDetalheCarregando(false);
    }
  }

  function resetarSelecao() {
    setModeloEscolhidoId('');
    setSelectedModeloId(null);
    setSelectedModelo(null);
    setPerguntasAnamnese([]);
    setRespostas({});
    setPreenchimentoEditandoId(null);
  }

  return (
    <div className="py-2 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Histórico de Anamnese</h2>
          <p className="text-sm text-gray-500">Formulários e histórico de saúde do paciente.</p>
        </div>
        <button
          className="px-5 py-2.5 rounded-lg text-white font-medium bg-[#0095DA] hover:bg-blue-600 transition-colors shadow-sm"
          onClick={() => setShowSelectModelo(true)}
        >
          Preencher Nova
        </button>
      </div>

      {/* Lista de Anamneses */}
      {anamnesesPreenchidas.length > 0 ? (
        <div className="grid grid-cols-1 gap-3">
          {anamnesesPreenchidas.map((a, idx) => (
            <div key={a.id} className="relative overflow-hidden w-full border border-gray-200 rounded-xl bg-white px-4 py-3 flex items-center justify-between hover:shadow-sm transition-shadow">
              <span className="absolute left-0 top-0 h-full w-1.5" style={{ backgroundColor: cardColors[(Number(a.modelo_id || a.modeloId || idx)) % cardColors.length] }} aria-hidden="true" />
              <div className="min-w-0 flex-1 ml-2">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} className="text-gray-400" />
                  <div className="text-sm font-bold text-gray-800 truncate">{a.modelo_nome || a.modeloNome}</div>
                </div>
                <div className="text-xs text-gray-500 font-medium">Preenchido em: {new Date(a.created_at || a.preenchidoEm).toLocaleString('pt-BR')}</div>
              </div>
              
              {/* Botões de Ação */}
              <div className="ml-4 shrink-0 flex items-center gap-1.5">
                <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors" title="Visualizar Detalhes" onClick={() => abrirDetalhePreenchimento(a.id)}>
                  <FileText size={18} />
                </button>
                <button className="p-2 rounded-lg hover:bg-green-50 text-green-600 transition-colors" title="Imprimir" onClick={() => toast.info('Função de impressão em desenvolvimento')}>
                  <Printer size={18} />
                </button>
                <button className="p-2 rounded-lg hover:bg-blue-50 text-[#0095DA] transition-colors" title="Editar" onClick={async () => {
                  try {
                    const { data } = await axios.get(`/api/anamnese/preenchimentos/${a.id}`);
                    const mId = data?.preenchimento?.modelo_id;
                    const modelo = modelosAnamnese.find(x => Number(x.id) === Number(mId));
                    
                    setSelectedModeloId(mId);
                    setSelectedModelo(modelo || null);
                    setModeloEscolhidoId(String(mId || ''));
                    
                    const map = {};
                    (data?.respostas || []).forEach(r => { map[String(r.pergunta_id)] = { opcao: r.opcao || null, texto: r.texto || '' }; });
                    setRespostas(map);
                    
                    try {
                      const { data: qs } = await axios.get(`/api/anamnese/modelos/${mId}/perguntas`);
                      setPerguntasAnamnese(Array.isArray(qs) ? qs : []);
                    } catch { setPerguntasAnamnese([]); }
                    
                    setShowSelectModelo(true);
                    setPreenchimentoEditandoId(a.id);
                  } catch { toast.error("Erro ao carregar dados para edição."); }
                }}>
                  <Pencil size={18} />
                </button>
                <button className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors" title="Remover" onClick={() => setConfirmRemoverId(a.id)}>
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-gray-500 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 py-10">
          <FileText size={40} className="mx-auto text-gray-300 mb-3" />
          <p>Nenhuma anamnese preenchida para este paciente.</p>
        </div>
      )}

      {/* MODAL: Selecionar e Preencher Anamnese */}
      {showSelectModelo && (
        <Modal 
          isOpen={showSelectModelo} 
          onClose={() => { setShowSelectModelo(false); resetarSelecao(); }} 
          shouldCloseOnOverlayClick={false} 
          shouldCloseOnEsc={false} 
          size="md"
        >
          <div className="p-2">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{preenchimentoEditandoId ? 'Editar Anamnese' : 'Nova Anamnese'}</h3>
            
            <div className="group mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Selecione o Modelo Base</label>
              <select
                className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0095DA] transition-colors"
                value={modeloEscolhidoId}
                disabled={preenchimentoEditandoId != null}
                onChange={async (e) => {
                  const id = e.target.value;
                  setModeloEscolhidoId(id);
                  setSelectedModeloId(id ? Number(id) : null);
                  
                  if (id) {
                    const m = (modelosAnamnese || []).find(x => String(x.id) === String(id));
                    setSelectedModelo(m || null);
                    try {
                      const { data } = await axios.get(`/api/anamnese/modelos/${id}/perguntas`);
                      setPerguntasAnamnese(Array.isArray(data) ? data : []);
                    } catch { setPerguntasAnamnese([]); }
                  } else {
                    setSelectedModelo(null);
                    setPerguntasAnamnese([]);
                  }
                }}
              >
                <option value="">Selecione…</option>
                {modelosAnamnese.map(m => (
                  <option key={m.id} value={m.id}>{m.nome}</option>
                ))}
              </select>
            </div>

            {selectedModelo && (
              <div className="mt-6 border-t pt-4">
                <div className="text-sm font-medium text-[#0095DA] mb-1">Preenchendo:</div>
                <div className="text-lg font-bold text-gray-800 mb-4">{selectedModelo?.nome}</div>
                
                <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-2 custom-scrollbar">
                  {perguntasAnamnese.length === 0 ? (
                    <div className="text-gray-500 text-sm">Nenhuma pergunta configurada neste modelo.</div>
                  ) : (
                    perguntasAnamnese.map((p, i) => (
                      <div key={p.id} className="border border-gray-100 bg-gray-50/50 rounded-lg p-4 shadow-sm">
                        <div className="flex gap-2 items-start mb-2">
                          <span className="bg-[#0095DA] text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full shrink-0">
                            {i + 1}
                          </span>
                          <div className="font-medium text-gray-800 mt-0.5">{p.titulo}</div>
                        </div>
                        <div className="pl-8">
                          {renderPergunta(p)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button 
                    className="px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors" 
                    onClick={() => { setShowSelectModelo(false); resetarSelecao(); }}
                  >
                    Cancelar
                  </button>
                  <button 
                    className="px-6 py-2 rounded-lg text-white font-medium bg-[#0095DA] hover:bg-blue-600 disabled:opacity-50 transition-colors shadow-sm" 
                    disabled={savingAnamnese || !selectedModelo} 
                    onClick={async () => {
                      if (preenchimentoEditandoId) {
                        setSavingAnamnese(true);
                        try {
                          await axios.put(`/api/anamnese/preenchimentos/${preenchimentoEditandoId}`, { respostas });
                          const { data } = await axios.get(`/api/anamnese/preenchimentos/paciente/${pacienteEditando.id}`);
                          setAnamnesesPreenchidas(Array.isArray(data) ? data : []);
                          toast.success('Anamnese atualizada.');
                          setShowSelectModelo(false);
                          resetarSelecao();
                        } catch (e) {
                          toast.error(e?.response?.data?.error || 'Falha ao atualizar anamnese.');
                        } finally {
                          setSavingAnamnese(false);
                        }
                      } else {
                        await salvarAnamnesePaciente();
                      }
                    }}
                  >
                    {savingAnamnese ? 'Salvando…' : 'Salvar Anamnese'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* MODAL: Visualizar Detalhes */}
      {detalheAbertoId != null && (
        <Modal isOpen={true} onClose={() => setDetalheAbertoId(null)} size="md">
          <div className="p-2">
            <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-3">Visualização de Anamnese</h3>
            {detalheCarregando ? (
              <div className="text-gray-500 py-10 text-center animate-pulse">Carregando dados...</div>
            ) : detalheDados ? (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center border border-gray-100">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-bold">Modelo</div>
                    <div className="text-sm font-semibold text-[#0095DA]">{detalheDados?.preenchimento?.modelo_nome || '-'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-bold">Preenchido em</div>
                    <div className="text-sm font-medium text-gray-700">
                      {detalheDados?.preenchimento?.created_at ? new Date(detalheDados.preenchimento.created_at).toLocaleString('pt-BR') : '-'}
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                  {(detalheDados?.respostas || []).map((r, i) => (
                    <div key={r.id || i} className="border-b border-gray-100 pb-3 last:border-0">
                      <div className="text-xs text-gray-400 font-bold mb-1">PERGUNTA {i + 1}</div>
                      <div className="font-medium text-gray-800 mb-1">{r.titulo}</div>
                      
                      {r.tipo === 'texto' ? (
                        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded whitespace-pre-wrap">{r.texto || '-'}</div>
                      ) : (
                        <div className="text-sm text-gray-700 flex flex-col gap-1 mt-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${r.opcao === 'sim' ? 'bg-green-100 text-green-700' : r.opcao === 'nao' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                              {r.opcao === 'sim' ? 'Sim' : r.opcao === 'nao' ? 'Não' : r.opcao === 'nao_sei' ? 'Não sei' : '-'}
                            </span>
                          </div>
                          {r.tipo === 'snn_texto' && r.texto && (
                            <div className="text-gray-600 text-sm italic mt-1 bg-yellow-50 p-2 rounded border border-yellow-100">
                              <span className="font-medium">Obs:</span> {r.texto}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="flex justify-end mt-6 pt-4 border-t">
              <button className="px-5 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors" onClick={() => setDetalheAbertoId(null)}>Fechar Visualização</button>
            </div>
          </div>
        </Modal>
      )}

      {/* MODAL: Confirmar Exclusão */}
      {confirmRemoverId != null && (
        <Modal isOpen={true} onClose={() => setConfirmRemoverId(null)} size="sm">
          <div className="p-2 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash size={30} className="text-red-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-800 mb-2">Excluir Anamnese?</h4>
            <p className="text-sm text-gray-500 mb-6">Esta ação não pode ser desfeita. Tem certeza que deseja remover este histórico?</p>
            
            <div className="flex justify-center gap-3">
              <button 
                className="px-5 py-2.5 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors w-full" 
                onClick={() => setConfirmRemoverId(null)}
              >
                Cancelar
              </button>
              <button
                className="px-5 py-2.5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm w-full"
                onClick={async () => {
                  try {
                    await axios.delete(`/api/anamnese/preenchimentos/${confirmRemoverId}`);
                    const { data } = await axios.get(`/api/anamnese/preenchimentos/paciente/${pacienteEditando.id}`);
                    setAnamnesesPreenchidas(Array.isArray(data) ? data : []);
                    toast.success('Anamnese removida.');
                  } catch (e) {
                    toast.error(e?.response?.data?.error || 'Falha ao remover.');
                  } finally {
                    setConfirmRemoverId(null);
                  }
                }}
              >
                Sim, Excluir
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}